import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import { Search, Trash2, ExternalLink } from 'lucide-react';
import Pagination from '../../components/admin/Pagination';

function AdminWorkspaces() {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = parseInt(searchParams.get('page')) || 1;

    const [data, setData] = useState({ data: [], total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            if (searchTerm !== debouncedSearch) {
                setSearchParams(prev => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    return params;
                });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        loadWorkspaces();
    }, [currentPage, debouncedSearch]);

    const loadWorkspaces = async () => {
        try {
            setLoading(true);
            const response = await adminManagementService.getWorkspaces({
                page: currentPage,
                limit: 20,
                search: debouncedSearch
            });
            setData(response);
        } catch (error) {
            console.error('Failed to load workspaces', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setSearchParams({ page: newPage });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this workspace?')) return;
        try {
            await adminManagementService.deleteWorkspace(id);
            loadWorkspaces();
        } catch (error) {
            alert('Failed to delete workspace');
        }
    };

    const workspaces = data.data || [];

    if (loading) return <div className="admin-loading">Loading workspaces...</div>;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <h2>Workspace Management ({data.total})</h2>
                <div className="admin-search-bar">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search workspaces..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Workspace</th>
                            <th>Owner</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {workspaces.map(ws => (
                            <tr key={ws.id}>
                                <td>
                                    <Link to={`/admin/workspaces/${ws.id}`} className="ws-cell">
                                        <span className="ws-name">{ws.name}</span>
                                        <span className="ws-slug">{ws.slug}</span>
                                    </Link>
                                </td>
                                <td>
                                    <div className="owner-cell">
                                        <span>{ws.owner?.email || 'Unknown'}</span>
                                    </div>
                                </td>
                                <td>
                                    {ws.isActive ? (
                                        <span className="status-badge success">Active</span>
                                    ) : (
                                        <span className="status-badge danger">Inactive</span>
                                    )}
                                </td>
                                <td>{new Date(ws.createdAt).toLocaleDateString()}</td>
                                <td>
                                    <div className="action-row">
                                        <button className="icon-btn" onClick={() => window.open(`/workspaces/${ws.id}`, '_blank')}>
                                            <ExternalLink size={18} />
                                        </button>
                                        <button className="icon-btn delete" onClick={() => handleDelete(ws.id)}>
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={data.totalPages}
                onPageChange={handlePageChange}
            />
        </div>
    );
}

export default AdminWorkspaces;
